import React from 'react';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import mergeRefs from '../../utils/react/merge-refs';

interface Props {
  height?: string;
  /**
   * Perform the enter transition when it first mounts if `in` is also `true`.
   * @default true
   */
  appear?: boolean;
  /**
   * A single child content element.
   */
  children: React.ReactElement;
  /**
   * If `true`, the component will transition in.
   */
  in: boolean;
  /**
   * The duration for the transition in ms
   * @default 225
   */
  timeout?: number;
  /**
   * A callback that is fired, when the animation is in entering state
   */
  onEntering?: (node: HTMLElement, appearing: boolean) => void;
  /**
   * A callback that is fired, when the animation is in enter state
   */
  onEnter?: (node: HTMLElement, appearing: boolean) => void;
  /**
   * A callback that is fired, when the animation is in entered state
   */
  onEntered?: (node: HTMLElement, appearing: boolean) => void;
  /**
   * A callback that is fired, when the animation is in exiting state
   */
  onExiting?: (node: HTMLElement) => void;
  /**
   * A callback that is fired, when the animation is in exit state
   */
  onExit?: (node: HTMLElement) => void;
  /**
   * A callback that is fired, when the animation is in exited state
   */
  onExited?: (node: HTMLElement) => void;
  /**
   * If true, the node will be removed from the dom, once the animation has finished
   */
  unmountOnExit?: boolean;
}

/**
 * The Grow component is used to apply a grow transition to the inner element using React Transition Group
 */
export const ExpandCollapse = React.forwardRef<HTMLDivElement, Props>(function Grow(props, ref) {
  const {
    height,
    onExit,
    onEnter,
    children,
    onExited,
    onEntered,
    onExiting,
    onEntering,
    in: inProp,
    appear = true,
    timeout = 225,
    unmountOnExit
  } = props;

  const child = React.Children.only(children);

  const nodeRef = React.useRef<HTMLElement>(null);

  const handleEnter = React.useCallback(
    (appearing: boolean) => {
      const node = nodeRef.current;

      if (node) {
        // So the animation always start from the start - it is an issue with react-transition-group
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        node.scrollTop = 0;

        node.style.maxHeight = '0';

        onEnter?.(node, appearing);
      }
    },
    [onEnter]
  );

  const handleEntering = React.useCallback(
    (appearing: boolean) => {
      const node = nodeRef.current;

      if (node) {
        node.style.maxHeight = height ?? '400px';

        onEntering?.(node, appearing);
      }
    },
    [height, onEntering]
  );

  const handleEntered = React.useCallback(
    (appearing: boolean) => {
      const node = nodeRef.current;

      if (node) {
        node.style.maxHeight = height ?? '400px';

        onEntered?.(node, appearing);
      }
    },
    [height, onEntered]
  );

  const handleExit = React.useCallback(() => {
    const node = nodeRef.current;

    if (node) {
      node.style.maxHeight = height ?? '400px';

      onExit?.(node);
    }
  }, [height, onExit]);

  const handleExiting = React.useCallback(() => {
    const node = nodeRef.current;

    if (node) {
      node.style.maxHeight = '0';

      onExiting?.(node);
    }
  }, [onExiting]);

  const handleExited = React.useCallback(() => {
    const node = nodeRef.current;

    if (node) {
      node.style.maxHeight = '0';

      onExited?.(node);
    }
  }, [onExited]);

  return (
    <Transition
      in={inProp}
      appear={appear}
      timeout={timeout}
      nodeRef={nodeRef}
      onExit={handleExit}
      onEnter={handleEnter}
      onExited={handleExited}
      onEntered={handleEntered}
      onExiting={handleExiting}
      onEntering={handleEntering}
      unmountOnExit={unmountOnExit}
    >
      {(status: TransitionStatus) => {
        return React.cloneElement(child, {
          ...child.props,
          ref: mergeRefs((child as any).ref, nodeRef, ref),
          style: {
            overflow: 'hidden',
            maxHeight: 0,
            transition: 'max-height 250ms',
            ...child.props.style
          }
        });
      }}
    </Transition>
  );
});

export default ExpandCollapse;
